import { useState, useEffect, useRef, useCallback } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { NavigationContextIdentityProvider } from './context/NavigationContextIdentity'
import ProductKnowledge from './pages/productKnowledge/ProductKnowledge'
import ProductKnowledgeContextLink from './components/ProductKnowledgeContextLink'
// Estimation is a fully separate app/codebase (its own AuthProvider, its own
// Supabase project) mounted here only as a route tree. Only its exported
// <Routes> tree (App.jsx with BrowserRouter removed, per the approved
// integration slice) is imported -- nothing else from Estimation is touched.
import EstimationApp from './estimation/App'
import { useProjectAuth } from './pages/useProjectAuth'
import LoginPage from './pages/LoginPage'
import LandingPage from './pages/CostSpaceLanding'
import TrialRequestModal from './pages/TrialRequestModal'
import SubscriptionGate from './pages/SubscriptionGate'
import UsersPage from './pages/UsersPage'
import CurrencyExchangeRatesPage from './pages/CurrencyExchangeRatesPage'
import SubscriptionManagementPage from './pages/SubscriptionManagementPage'
import PermissionsPage from './pages/PermissionsPage'
import Organizations from './pages/Organizations'
import Projects from './pages/Projects'
import CostCodes from './pages/CostCodes'
import BOQ from './pages/BOQ'
import Budget from './pages/Budget'
import Dashboard from './pages/Dashboard'
import PurchaseOrders from './pages/PurchaseOrders'
import PurchaseRequisitions from './pages/PurchaseRequisitions'
import RFQQuotations from './pages/RFQQuotations'
import Store from './pages/Store'
import InventoryManagement from './pages/InventoryManagement'
import Subcontracts from './pages/Subcontracts'
import IPC from './pages/IPC'
import Payroll from './pages/Payroll'
import Finance from './pages/Finance'
import FinanceLandingPage from './pages/FinanceLandingPage'
import IndirectBudget from './pages/IndirectBudget'
import Accrual from './pages/Accrual'
import DirectForecast from './pages/DirectForecast'
import IndirectForecast from './pages/IndirectForecast'
import ReleaseManagement from './pages/ReleaseManagement'
import CostControl from './pages/CostControl'
import POReconciliation from './pages/POReconciliation'
import CostAssessment from './pages/CostAssessment'
import BudgetSummary from './pages/BudgetSummary'
import PettyCash from './pages/PettyCash'
import AlertsVariances from './pages/AlertsVariances'
import DocumentCenter from './pages/DocumentCenter'
import DocumentGovernance from './pages/DocumentGovernance'
import LabourProductivity from './pages/LabourProductivity'
import EquipmentProductivity from './pages/EquipmentProductivity'
import SiteAllocationDraft from './pages/SiteAllocationDraft'
import QSQuantityTracking from './pages/QSQuantityTracking'
import RisksChanges from './pages/RisksChanges'
import FinalAccount from './pages/FinalAccount'
import RiskRegister from './pages/RiskRegister'
import EOTRegister from './pages/EOTRegister'
import TechnicalChangeRegister from './pages/TechnicalChangeRegister'
import RateAnalysis from './pages/RateAnalysis'
import HomeWorkspace from './pages/HomeWorkspace'
import HomeLaunchpad from './pages/HomeLaunchpad'
import ExecutiveReport from './pages/ExecutiveReport'
import { NAV, GROUPS, GROUP_LABELS } from './lib/navigationConfig'
import ExecutiveReportConfig from './pages/ExecutiveReportConfig'
import VendorMaster from './pages/VendorMaster'
import MaterialMaster from './pages/MaterialMaster'
import RetentionManagement from './pages/RetentionManagement'
import MonthlyActualCost from './pages/MonthlyActualCost'
import AnalyticsStudio from './pages/AnalyticsStudio'
import Scheduling from './pages/Scheduling'
import { supabase } from './lib/supabase'

// ── CostSpace Logo mark
const QubexMark = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100">
    <rect width="100" height="100" rx="24" fill="#0B3D91"/>
    <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/>
    <circle cx="73" cy="50" r="7.5" fill="#5DADE2"/>
  </svg>
)

const Icon = ({ name, size = 14 }) => {
  const s = { width: size, height: size, flexShrink: 0 }
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: '1.4', strokeLinecap: 'round', strokeLinejoin: 'round' }
  const icons = {
    workspace:           <svg style={s} viewBox="0 0 16 16" {...p}><rect x="1" y="1" width="6" height="9" rx="1"/><rect x="9" y="1" width="6" height="4" rx="1"/><rect x="9" y="7" width="6" height="8" rx="1"/><rect x="1" y="12" width="6" height="3" rx="1"/></svg>,
    dashboard:           <svg style={s} viewBox="0 0 16 16" {...p}><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>,
    organizations:       <svg style={s} viewBox="0 0 16 16" {...p}><rect x="5" y="1" width="6" height="4" rx="0.5"/><rect x="1" y="11" width="5" height="4" rx="0.5"/><rect x="10" y="11" width="5" height="4" rx="0.5"/><line x1="8" y1="5" x2="8" y2="8.5"/><line x1="3.5" y1="8.5" x2="12.5" y2="8.5"/><line x1="3.5" y1="8.5" x2="3.5" y2="11"/><line x1="12.5" y1="8.5" x2="12.5" y2="11"/></svg>,
    projects:            <svg style={s} viewBox="0 0 16 16" {...p}><path d="M2 4h3l1.5-2h6L14 4v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4z"/><line x1="5" y1="8" x2="11" y2="8"/><line x1="5" y1="11" x2="9" y2="11"/></svg>,
    'cost-codes':        <svg style={s} viewBox="0 0 16 16" {...p}><polyline points="4,5 2,8 4,11"/><polyline points="12,5 14,8 12,11"/><line x1="7" y1="3" x2="9" y2="13"/></svg>,
    boq:                 <svg style={s} viewBox="0 0 16 16" {...p}><rect x="2" y="2" width="12" height="12" rx="1"/><line x1="2" y1="6" x2="14" y2="6"/><line x1="2" y1="10" x2="14" y2="10"/><line x1="6" y1="6" x2="6" y2="14"/></svg>,
    budget:              <svg style={s} viewBox="0 0 16 16" {...p}><rect x="1.5" y="3" width="13" height="10" rx="1"/><line x1="8" y1="5.5" x2="8" y2="10.5"/><line x1="5.5" y1="8" x2="10.5" y2="8"/></svg>,
    indirect:            <svg style={s} viewBox="0 0 16 16" {...p}><rect x="1.5" y="3" width="13" height="10" rx="1"/><path d="M5 8.5 Q8 5.5 11 8.5"/><line x1="5" y1="8.5" x2="5" y2="10.5"/><line x1="11" y1="8.5" x2="11" y2="10.5"/></svg>,
    'budget-summary':    <svg style={s} viewBox="0 0 16 16" {...p}><rect x="2" y="2" width="12" height="12" rx="1"/><polyline points="4.5,9.5 6.5,7 9,9 13,5"/></svg>,
    'purchase-orders':   <svg style={s} viewBox="0 0 16 16" {...p}><path d="M2 2h1.5l1.8 7h7l1.7-5H5.3"/><circle cx="7" cy="13.5" r="1"/><circle cx="11" cy="13.5" r="1"/></svg>,
    store:               <svg style={s} viewBox="0 0 16 16" {...p}><rect x="3" y="7" width="10" height="7" rx="0.5"/><path d="M1 7l2-4h10l2 4"/><line x1="1" y1="7" x2="15" y2="7"/><line x1="8" y1="7" x2="8" y2="14"/></svg>,
    subcontracts:        <svg style={s} viewBox="0 0 16 16" {...p}><path d="M4 13V8a2 2 0 0 1 2-2h6"/><polyline points="9,4 12,6 9,8"/><path d="M12 3v10"/></svg>,
    ipc:                 <svg style={s} viewBox="0 0 16 16" {...p}><rect x="3" y="1.5" width="10" height="13" rx="1"/><polyline points="6,7 7.5,9 10.5,5.5"/><line x1="5" y1="11.5" x2="11" y2="11.5"/></svg>,
    payroll:             <svg style={s} viewBox="0 0 16 16" {...p}><circle cx="8" cy="5.5" r="2.5"/><path d="M3 14c0-2.76 2.24-5 5-5s5 2.24 5 5"/></svg>,
    finance:             <svg style={s} viewBox="0 0 16 16" {...p}><circle cx="8" cy="8" r="6.5"/><line x1="8" y1="4" x2="8" y2="5.5"/><line x1="8" y1="10.5" x2="8" y2="12"/><path d="M6 7c0-1 .9-1.5 2-1.5s2 .5 2 1.5-1 1.5-2 1.5-2 .5-2 1.5.9 1.5 2 1.5 2-.5 2-1.5"/></svg>,
    accrual:             <svg style={s} viewBox="0 0 16 16" {...p}><polyline points="1.5,12 5,7 8,9.5 11,5 14.5,8"/><line x1="14.5" y1="8" x2="14.5" y2="12"/><line x1="1.5" y1="12" x2="14.5" y2="12"/></svg>,
    forecasts:           <svg style={s} viewBox="0 0 16 16" {...p}><polyline points="1.5,12 5,7 8,9.5 11,5 14.5,8"/><polyline points="11,5 14.5,5 14.5,8"/></svg>,
    'petty-cash':        <svg style={s} viewBox="0 0 16 16" {...p}><rect x="1.5" y="5" width="13" height="8" rx="1"/><path d="M5 5V4a3 3 0 0 1 6 0v1"/><circle cx="8" cy="9" r="1.5"/></svg>,
    costcontrol:         <svg style={s} viewBox="0 0 16 16" {...p}><rect x="1.5" y="1.5" width="4.5" height="13" rx="0.5"/><rect x="5.75" y="5" width="4.5" height="9.5" rx="0.5"/><rect x="10" y="8.5" width="4.5" height="6" rx="0.5"/></svg>,
    'po-reconciliation': <svg style={s} viewBox="0 0 16 16" {...p}><rect x="2" y="2" width="5.5" height="7" rx="0.5"/><rect x="8.5" y="7" width="5.5" height="7" rx="0.5"/><line x1="4.75" y1="9" x2="4.75" y2="14"/><line x1="11.25" y1="2" x2="11.25" y2="7"/><polyline points="3.25,14 4.75,12.5 6.25,14"/><polyline points="9.75,2 11.25,3.5 12.75,2"/></svg>,
    'cost-assessment':   <svg style={s} viewBox="0 0 16 16" {...p}><circle cx="7" cy="7" r="5"/><line x1="10.5" y1="10.5" x2="14" y2="14"/><line x1="5" y1="7" x2="9" y2="7"/><line x1="7" y1="5" x2="7" y2="9"/></svg>,
    'rate-analysis':     <svg style={s} viewBox="0 0 16 16" {...p}><polyline points="1.5,12 5,7 8,9.5 11,5 14.5,3"/><circle cx="5" cy="7" r="1" fill="currentColor" stroke="none"/><circle cx="8" cy="9.5" r="1" fill="currentColor" stroke="none"/><circle cx="11" cy="5" r="1" fill="currentColor" stroke="none"/></svg>,
    alerts:              <svg style={s} viewBox="0 0 16 16" {...p}><path d="M8 1.5 L14.5 13 H1.5 Z"/><line x1="8" y1="6" x2="8" y2="9.5"/><circle cx="8" cy="11.5" r="0.6" fill="currentColor" stroke="none"/></svg>,
    'final-account':     <svg style={s} viewBox="0 0 16 16" {...p}><rect x="2" y="1.5" width="12" height="13" rx="1"/><line x1="5" y1="5.5" x2="11" y2="5.5"/><line x1="5" y1="8" x2="11" y2="8"/><line x1="5" y1="10.5" x2="8.5" y2="10.5"/></svg>,
    'risk-register':     <svg style={s} viewBox="0 0 16 16" {...p}><path d="M8 1.5 L14.5 13 H1.5 Z"/><line x1="8" y1="6" x2="8" y2="9.5"/><circle cx="8" cy="11.5" r="0.6" fill="currentColor" stroke="none"/></svg>,
    'eot-register':      <svg style={s} viewBox="0 0 16 16" {...p}><circle cx="8" cy="8" r="6.5"/><line x1="8" y1="4" x2="8" y2="8"/><line x1="8" y1="8" x2="11" y2="8"/><line x1="4" y1="1.5" x2="4" y2="3.5"/><line x1="12" y1="1.5" x2="12" y2="3.5"/></svg>,
    'change-register':   <svg style={s} viewBox="0 0 16 16" {...p}><path d="M3 8h10M9 4l4 4-4 4"/><path d="M5 4l-2 2 2 2" strokeDasharray="2"/></svg>,
    permissions:         <svg style={s} viewBox="0 0 16 16" {...p}><rect x="2" y="2" width="12" height="12" rx="2"/><line x1="5" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="9" y2="9"/></svg>,
    'inventory-management': <svg style={s} viewBox="0 0 16 16" {...p}><path d="M2 5.5l6-3.5 6 3.5v5L8 14l-6-3.5z"/><path d="M2 5.5L8 9l6-3.5"/><line x1="8" y1="9" x2="8" y2="14"/></svg>,
    'material-master':   <svg style={s} viewBox="0 0 16 16" {...p}><rect x="3" y="7" width="10" height="7" rx="0.5"/><path d="M1 7l2-4h10l2 4"/><line x1="8" y1="7" x2="8" y2="14"/><circle cx="8" cy="10.5" r="1.2" fill="currentColor" stroke="none"/></svg>,
    users:               <svg style={s} viewBox="0 0 16 16" {...p}><circle cx="6" cy="5" r="2.5"/><path d="M1 14c0-2.76 2.24-5 5-5s5 2.24 5 5"/><path d="M12 7c1.1 0 2 .9 2 2s-.9 2-2 2"/><path d="M14 14c0-1.66-1.34-3-3-3"/></svg>,
    search:              <svg style={s} viewBox="0 0 16 16" {...p}><circle cx="6.5" cy="6.5" r="4.5"/><line x1="9.5" y1="9.5" x2="14" y2="14"/></svg>,
    bell:                <svg style={s} viewBox="0 0 16 16" {...p}><path d="M8 1.5a5 5 0 015 5v3l1.5 2H1.5L3 9.5v-3a5 5 0 015-5z"/><path d="M6.5 13.5a1.5 1.5 0 003 0"/></svg>,
    collapse:            <svg style={s} viewBox="0 0 16 16" {...p}><polyline points="10,4 6,8 10,12"/></svg>,
    expand:              <svg style={s} viewBox="0 0 16 16" {...p}><polyline points="6,4 10,8 6,12"/></svg>,
    x:                   <svg style={s} viewBox="0 0 16 16" {...p}><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></svg>,
  }
  return icons[name] || <svg style={s} viewBox="0 0 16 16"/>
}

function NavBadge({ count, red }) {
  return (
    <span style={{
      background: red ? '#DC2626' : '#1A7A4A',
      color: '#fff', borderRadius: 10,
      padding: '1px 6px', fontSize: 9, fontWeight: 700, lineHeight: '16px',
    }}>{count}</span>
  )
}


function NavItem({ item, active, onClick, pendingCount }) {
  const [hov, setHov] = useState(false)
  const badge = item.key === 'pending-approvals' ? pendingCount : null
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ width:'100%', display:'flex', alignItems:'center', gap:9, padding:'7px 14px 7px 20px', background: active?'#252521':hov?'#252521':'transparent', border:'none', borderLeft:`2px solid ${active?'#1A7A4A':'transparent'}`, cursor:'pointer', textAlign:'left', transition:'background 0.1s' }}>
      <span style={{ color: active?'#1A7A4A':hov?'#9A948C':'#6B655C', transition:'color 0.1s', flexShrink:0 }}>
        <Icon name={item.key} size={14}/>
      </span>
      <span style={{ fontSize:12, fontWeight:active?600:400, color:active?'#F5F1EA':hov?'#F5F1EA':'#9A948C', flex:1, textAlign:'left', lineHeight:1.2, transition:'color 0.1s' }}>
        {item.label}
      </span>
      {badge > 0 && <NavBadge count={badge} red={item.key==='pending-approvals'}/>}
    </button>
  )
}

function UserAvatarButton() {
  const { user, profile, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const name     = profile?.full_name || user?.email || 'User'
  const initials = name.includes('@') ? name[0].toUpperCase() : name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()
  return (
    <div style={{ position:'relative', width:'100%', padding:'8px 6px 10px', borderTop:'1px solid #2E2C29' }}>
      <div onClick={() => setOpen(v => !v)} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'4px 6px', borderRadius:6, transition:'background .12s' }}
        onMouseEnter={e=>e.currentTarget.style.background='#252321'}
        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
        <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#1A7A4A,#2FA866)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
          {initials}
        </div>
        <div style={{ overflow:'hidden', flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#D0CCC6', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
          <div style={{ fontSize:9, color:'#4A4845', marginTop:1 }}>{profile?.role || 'User'}</div>
        </div>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#4A4845" strokeWidth="1.5"><polyline points="2,4 5,7 8,4"/></svg>
      </div>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:49 }}/>
          <div style={{ position:'absolute', bottom:'calc(100% + 4px)', left:6, right:6, zIndex:50, background:'#1C1A18', border:'1px solid #2E2C29', borderRadius:8, padding:'6px 0', boxShadow:'0 -4px 16px rgba(0,0,0,.3)' }}>
            <div style={{ padding:'6px 12px 8px', borderBottom:'1px solid #2E2C29' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#F5F1EA' }}>{name}</div>
              <div style={{ fontSize:10, color:'#6B655C', marginTop:1 }}>{profile?.role || 'User'}</div>
            </div>
            <button onClick={() => { setOpen(false); if(window.confirm('تسجيل الخروج؟')) signOut() }}
              style={{ width:'100%', textAlign:'left', padding:'7px 12px', fontSize:12, color:'#F87171', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Sign Out
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// COMMAND PALETTE (⌘K)
// ══════════════════════════════════════════════════════════
function CommandPalette({ open, onClose, onNavigate, selectedProject }) {
  const [query, setQuery] = useState('')
  const inputRef = useRef(null)
  const [selectedIdx, setSelectedIdx] = useState(0)

  useEffect(() => {
    if (open) { setQuery(''); setSelectedIdx(0); setTimeout(() => inputRef.current?.focus(), 50) }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowDown') setSelectedIdx(i => Math.min(i+1, results.length-1))
      if (e.key === 'ArrowUp')   setSelectedIdx(i => Math.max(i-1, 0))
      if (e.key === 'Enter' && results[selectedIdx]) {
        onNavigate(results[selectedIdx].key)
        onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, selectedIdx]) // eslint-disable-line

  const q = query.toLowerCase().trim()
  const results = q
    ? NAV.filter(n => n.label.toLowerCase().includes(q) || n.group.toLowerCase().includes(q))
    : NAV.slice(0, 8)

  if (!open) return null

  return (
    <div style={{ position:'fixed', inset:0, zIndex:400, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:72 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      {/* Backdrop */}
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.45)', backdropFilter:'blur(4px)' }} onClick={onClose}/>
      {/* Box */}
      <div style={{ position:'relative', zIndex:1, width:560, background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', boxShadow:'0 24px 64px rgba(0,0,0,.18)', overflow:'hidden' }}>
        {/* Input */}
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', borderBottom:'1px solid #E5E7EB' }}>
          <Icon name="search" size={16}/>
          <input ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIdx(0) }}
            placeholder="Search pages, modules…"
            style={{ flex:1, fontSize:16, border:'none', outline:'none', color:'#0F1017', background:'transparent', fontFamily:'inherit' }}
          />
          <span style={{ padding:'1px 5px', background:'#F3F4F6', border:'1px solid #E5E7EB', borderRadius:3, fontSize:9, color:'rgba(0,0,0,.35)' }}>ESC</span>
        </div>

        {/* Results */}
        <div>
          {!q && (
            <div style={{ padding:'6px 18px 4px', fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#9CA3AF' }}>
              Quick Navigation
            </div>
          )}
          {results.map((item, i) => (
            <div key={item.key}
              onClick={() => { onNavigate(item.key); onClose() }}
              onMouseEnter={() => setSelectedIdx(i)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 18px', cursor:'pointer', background: i===selectedIdx ? '#F3F4F6' : 'transparent', transition:'background .1s' }}>
              <div style={{ width:28, height:28, borderRadius:6, background:'#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center', color:'#6B7280', flexShrink:0 }}>
                <Icon name={item.key} size={14}/>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#0F1017' }}>{item.label}</div>
                <div style={{ fontSize:11, color:'#9CA3AF' }}>{item.group}</div>
              </div>
              <span style={{ fontSize:10, color:'#9CA3AF' }}>↵</span>
            </div>
          ))}
          {q && results.length === 0 && (
            <div style={{ padding:'24px 18px', textAlign:'center', color:'#9CA3AF', fontSize:13 }}>
              No results for "{query}"
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'10px 18px', borderTop:'1px solid #E5E7EB', display:'flex', gap:16 }}>
          {[['↑↓','Navigate'],['↵','Open'],['ESC','Close']].map(([k,l]) => (
            <span key={k} style={{ fontSize:10, color:'#9CA3AF', display:'flex', alignItems:'center', gap:4 }}>
              <span style={{ padding:'1px 5px', background:'#F3F4F6', border:'1px solid #E5E7EB', borderRadius:3, fontSize:9, fontWeight:600 }}>{k}</span>
              {l}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// NOTIFICATION TRAY
// ══════════════════════════════════════════════════════════
function NotificationTray({ open, onClose, onNavigate, projectId }) {
  const [notifs, setNotifs] = useState([])
  const [loading, setLoading] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    buildNotifications(projectId).then(n => {
      setNotifs(n)
      setUnreadCount(n.filter(x => !x.read).length)
      setLoading(false)
    })
  }, [open, projectId])

  async function buildNotifications(pid) {
    const items = []
    try {
      // Pending POs
      const { data: pendingPOs } = await supabase
        .from('purchase_orders').select('id,po_number,vendor,total_exc_vat,created_at')
        .eq('project_id', pid).eq('status','draft').order('created_at',{ascending:false}).limit(5)
      for (const po of (pendingPOs||[])) {
        items.push({
          id: 'po-'+po.id, type:'warning', read:false,
          title: `${po.po_number} pending approval`,
          sub: `${po.vendor} · SAR ${Number(po.total_exc_vat||0).toLocaleString('en-US',{maximumFractionDigits:0})}`,
          time: po.created_at,
          action: { label:'Review', page:'purchase-orders' },
        })
      }
      // Pending IPCs
      const { data: pendingIPCs } = await supabase
        .from('ipc_certificates').select('id,certificate_no,gross_amount,created_at,subcontracts(contract_no)')
        .eq('project_id', pid).in('status',['submitted','under_review']).order('created_at',{ascending:false}).limit(3)
      for (const ipc of (pendingIPCs||[])) {
        items.push({
          id: 'ipc-'+ipc.id, type:'info', read:false,
          title: `IPC ${ipc.certificate_no||''} awaiting review`,
          sub: `${ipc.subcontracts?.contract_no||''} · SAR ${Number(ipc.gross_amount||0).toLocaleString('en-US',{maximumFractionDigits:0})}`,
          time: ipc.created_at,
          action: { label:'Review IPC', page:'ipc' },
        })
      }
    } catch(e) { console.error(e) }
    return items.sort((a,b) => new Date(b.time)-new Date(a.time))
  }

  function timeAgo(ts) {
    if (!ts) return ''
    const diff = Date.now() - new Date(ts).getTime()
    const mins = Math.floor(diff/60000)
    if (mins < 60)  return `${mins}m ago`
    if (mins < 1440) return `${Math.floor(mins/60)}h ago`
    return `${Math.floor(mins/1440)}d ago`
  }

  if (!open) return null

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:299 }}/>
      <div style={{
        position:'fixed', top:0, right:0, width:360,
        height:'100vh', background:'#fff',
        borderLeft:'1px solid #E5E7EB', zIndex:300,
        display:'flex', flexDirection:'column',
        boxShadow:'-4px 0 24px rgba(0,0,0,.08)',
        transform:'translateX(0)', transition:'transform .22s cubic-bezier(.22,1,.36,1)',
      }}>
        {/* Header */}
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:14, fontWeight:700, color:'#0F1017' }}>Notifications</span>
            {unreadCount > 0 && (
              <span style={{ background:'#DC2626', color:'#fff', borderRadius:10, padding:'1px 6px', fontSize:10, fontWeight:700 }}>{unreadCount}</span>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {notifs.length > 0 && (
              <button onClick={() => setNotifs(n => n.map(x => ({...x, read:true})))}
                style={{ fontSize:11, color:'#6B7280', background:'none', border:'1px solid #E5E7EB', borderRadius:6, padding:'3px 8px', cursor:'pointer', fontFamily:'inherit' }}>
                Mark all read
              </button>
            )}
            <button onClick={onClose}
              style={{ width:28, height:28, borderRadius:6, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, cursor:'pointer' }}>
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {loading && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:40, gap:10, color:'#9CA3AF' }}>
              <div style={{ width:20, height:20, border:'2px solid #E5E7EB', borderTopColor:'#1A7A4A', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
              Loading…
            </div>
          )}
          {!loading && notifs.length === 0 && (
            <div style={{ padding:'60px 24px', textAlign:'center', color:'#9CA3AF' }}>
              <div style={{ fontSize:32, marginBottom:12 }}>🔔</div>
              <p style={{ fontSize:13, fontWeight:600, color:'#6B7280' }}>
                {projectId ? 'No pending actions' : 'Select a project to see notifications'}
              </p>
            </div>
          )}
          {!loading && notifs.map(n => (
            <div key={n.id}
              style={{ padding:'14px 18px', borderBottom:'1px solid #E5E7EB', display:'flex', gap:10, cursor:'pointer', background: n.read ? '#fff' : '#FAFBFF', borderLeft: n.read ? 'none' : '3px solid #2563EB', transition:'background .1s' }}
              onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background=n.read?'#fff':'#FAFBFF'}>
              <div style={{ width:7, height:7, borderRadius:'50%', background: n.type==='warning'?'#D97706':n.type==='error'?'#DC2626':'#2563EB', flexShrink:0, marginTop:5 }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12.5, fontWeight:600, color:'#0F1017', marginBottom:2, lineHeight:1.4 }}>{n.title}</div>
                <div style={{ fontSize:11, color:'#6B7280' }}>{n.sub}</div>
                <div style={{ fontSize:10, color:'#9CA3AF', marginTop:3 }}>{timeAgo(n.time)}</div>
                {n.action && (
                  <div style={{ display:'flex', gap:6, marginTop:8 }}>
                    <button onClick={() => { onNavigate(n.action.page); onClose() }}
                      style={{ padding:'3px 10px', borderRadius:4, fontSize:10, fontWeight:700, border:'1px solid rgba(26,122,74,.25)', background:'rgba(26,122,74,.08)', color:'#1A7A4A', cursor:'pointer', fontFamily:'inherit' }}>
                      {n.action.label}
                    </button>
                    <button onClick={() => setNotifs(prev => prev.map(x => x.id===n.id?{...x,read:true}:x))}
                      style={{ padding:'3px 10px', borderRadius:4, fontSize:10, fontWeight:600, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', cursor:'pointer', fontFamily:'inherit' }}>
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  )
}

// ══════════════════════════════════════════════════════════
// CONTEXTUAL HELP
// ══════════════════════════════════════════════════════════
const EVM_TERMS = [
  { term:'BAC',  full:'Budget at Completion',      def:'The total approved budget for the project. Your spending ceiling.' },
  { term:'EAC',  full:'Estimate at Completion',     def:'Forecasted total cost when the project finishes. EAC = Actual + Remaining estimate.' },
  { term:'EV',   full:'Earned Value',               def:'The budgeted value of work actually completed. EV = BAC × % Progress.' },
  { term:'CPI',  full:'Cost Performance Index',     def:'CPI = EV ÷ Actual Cost. >1 = under budget. <1 = over budget.' },
  { term:'VAC',  full:'Variance at Completion',     def:'VAC = BAC − EAC. Positive = saving. Negative = overrun.' },
  { term:'SPI',  full:'Schedule Performance Index', def:'SPI = EV ÷ Planned Value. >1 = ahead of schedule. <1 = behind.' },
  { term:'GP%',  full:'Gross Profit %',             def:'GP% = (Contract Value − EAC) ÷ Contract Value × 100.' },
  { term:'IPC',  full:'Interim Payment Certificate',def:'A certified payment claim from a subcontractor for work completed in a period.' },
  { term:'GRN',  full:'Goods Receipt Note',         def:'A document confirming materials have been received on site against a PO.' },
  { term:'BOQ',  full:'Bill of Quantities',         def:'A detailed list of work items with quantities and unit rates from the contract.' },
  { term:'PR',   full:'Purchase Requisition',       def:'An internal request to procure goods or services, before a PO is raised.' },
  { term:'RFQ',  full:'Request for Quotation',      def:'A formal document sent to vendors asking for pricing on goods or services.' },
]

const WORKFLOW_STEPS = [
  { icon:'📋', step:'PR', label:'Purchase Requisition', sub:'Raise a need' },
  { icon:'📊', step:'RFQ', label:'Request for Quotation', sub:'Get vendor prices' },
  { icon:'🛒', step:'PO', label:'Purchase Order', sub:'Approve & commit' },
  { icon:'📦', step:'GRN', label:'Goods Receipt', sub:'Confirm delivery' },
  { icon:'💰', step:'Invoice', label:'Invoice & Payment', sub:'Finance records' },
]

function ContextualHelp({ open, onClose }) {
  const [tab, setTab] = useState('glossary')
  if (!open) return null
  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:299 }}/>
      <div style={{ position:'fixed', top:0, right:0, width:380, height:'100vh', background:'#fff', borderLeft:'1px solid #E5E7EB', zIndex:300, display:'flex', flexDirection:'column', boxShadow:'-4px 0 24px rgba(0,0,0,.08)' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <span style={{ fontSize:14, fontWeight:700, color:'#0F1017' }}>Help & Reference</span>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:6, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16 }}>✕</button>
        </div>
        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
          {[{id:'glossary',label:'Glossary'},{id:'workflow',label:'Workflow'},{id:'quickstart',label:'Quick Start'}].map(t => (
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{ flex:1, padding:'8px', fontSize:11.5, fontWeight:tab===t.id?700:500, color:tab===t.id?'#0F1017':'#6B7280', background:'transparent', border:'none', borderBottom:`2px solid ${tab===t.id?'#1A7A4A':'transparent'}`, cursor:'pointer', fontFamily:'inherit' }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:16 }}>
          {tab === 'glossary' && (
            <div>
              <p style={{ fontSize:11, color:'#6B7280', marginBottom:12 }}>Hover over any metric in the dashboard to see its definition. Full glossary below:</p>
              {EVM_TERMS.map(t => (
                <div key={t.term} style={{ marginBottom:10, padding:'10px 12px', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:8 }}>
                  <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:4 }}>
                    <span style={{ fontSize:13, fontWeight:800, color:'#1A7A4A', minWidth:40 }}>{t.term}</span>
                    <span style={{ fontSize:11, color:'#6B7280' }}>{t.full}</span>
                  </div>
                  <p style={{ fontSize:12, color:'#374151' }}>{t.def}</p>
                </div>
              ))}
            </div>
          )}
          {tab === 'workflow' && (
            <div>
              <p style={{ fontSize:11, color:'#6B7280', marginBottom:14 }}>Standard procurement workflow in CostSpace:</p>
              <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                {WORKFLOW_STEPS.map((s,i) => (
                  <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:'#0F1017', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>{s.icon}</div>
                      {i < WORKFLOW_STEPS.length-1 && <div style={{ width:2, height:24, background:'#E5E7EB' }}/>}
                    </div>
                    <div style={{ paddingTop:6, paddingBottom:i < WORKFLOW_STEPS.length-1 ? 16 : 0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#0F1017' }}>{s.step} — {s.label}</div>
                      <div style={{ fontSize:11, color:'#6B7280' }}>{s.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:16, padding:'12px 14px', background:'rgba(26,122,74,.06)', border:'1px solid rgba(26,122,74,.2)', borderRadius:8 }}>
                <p style={{ fontSize:11, fontWeight:700, color:'#1A7A4A', marginBottom:4 }}>Subcontractor Payment Flow</p>
                <p style={{ fontSize:11, color:'#374151' }}>Subcontract → IPC Certificate → Approve → Finance Payment → Cash Out recorded</p>
              </div>
            </div>
          )}
          {tab === 'quickstart' && (
            <div>
              <p style={{ fontSize:11, color:'#6B7280', marginBottom:14 }}>Get started with CostSpace in 4 steps:</p>
              {[
                { n:1, title:'Select your project', sub:'Click on a project card from the My Workspace screen or Projects page', page:'projects' },
                { n:2, title:'Review the Dashboard', sub:'Check CPI, Gross Profit, EAC vs Budget, and Cash Position', page:'dashboard' },
                { n:3, title:'Create your first PO', sub:'Go to Purchase Orders → New PO, select a vendor and add line items', page:'purchase-orders' },
                { n:4, title:'Run the Cost Control Report', sub:'Go to Cost Control, select a month and refresh to see actuals vs budget', page:'costcontrol' },
              ].map((s,i) => (
                <div key={i} style={{ display:'flex', gap:10, marginBottom:14, padding:'12px', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:8 }}>
                  <div style={{ width:26, height:26, borderRadius:'50%', background:'#1A7A4A', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, flexShrink:0 }}>{s.n}</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#0F1017' }}>{s.title}</div>
                    <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ══════════════════════════════════════════════════════════
// SIDEBAR
// ══════════════════════════════════════════════════════════
function TwoLevelSidebar({ page, setPage, selectedProject, onProjectClick, pendingCount }) {
  const activeGroup = NAV.find(n => n.key === page)?.group || GROUPS[0]
  const [panelGroup, setPanelGroup] = useState(activeGroup)
  const [collapsed, setCollapsed] = useState(false)
  const [hoveredGroup, setHoveredGroup] = useState(null)

  useEffect(() => {
    const g = NAV.find(n => n.key === page)?.group
    if (g) setPanelGroup(g)
  }, [page])

  const panelItems = NAV.filter(n => n.group === panelGroup)

  return (
    <aside style={{ display:'flex', flexShrink:0, height:'100%' }}>
      {/* ── LEFT RAIL (60px) ── */}
      <div style={{ width:60, background:'#1C1A18', borderRight:'1px solid #2E2C29', display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0, position:'relative', zIndex:300 }}>
        <div style={{ height:52, width:'100%', display:'flex', alignItems:'center', justifyContent:'center', borderBottom:'1px solid #2E2C29' }}>
          <QubexMark size={24}/>
        </div>
        <nav style={{ flex:1, overflowY:'auto', overflowX:'hidden', width:'100%', padding:'4px 0', display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
          {GROUPS.map(group => {
            const items        = NAV.filter(n => n.group === group)
            const isActive     = group === panelGroup
            const hasCurrentPage = items.some(i => i.key === page)
            const badge        = group === 'Workflow' ? pendingCount : 0
            const iconKey      = items[0]?.key
            const isHovered    = hoveredGroup === group
            return (
              <div key={group} style={{ position:'relative', width:'100%' }}
                onMouseEnter={() => setHoveredGroup(group)}
                onMouseLeave={() => setHoveredGroup(null)}>
                <button title={group}
                  onClick={() => { setPanelGroup(group); if (collapsed) setCollapsed(false) }}
                  style={{ position:'relative', width:'100%', padding:'6px 0 5px', display:'flex', flexDirection:'column', alignItems:'center', gap:3, background: isActive && !collapsed ? '#252321' : 'transparent', border:'none', borderLeft:`2px solid ${hasCurrentPage?'#1A7A4A':isActive&&!collapsed?'#4A4845':'transparent'}`, cursor:'pointer', color: hasCurrentPage?'#1A7A4A':isActive?'#D0CCC6':'#6B655C', transition:'all 0.12s' }}>
                  <Icon name={iconKey} size={15}/>
                  <span style={{ fontSize:8.5, fontWeight:600, letterSpacing:'.02em', color: hasCurrentPage?'#1A7A4A':isActive?'#9A948C':'#4A4845', lineHeight:1 }}>
                    {GROUP_LABELS[group]}
                  </span>
                  {badge > 0 && <span style={{ position:'absolute', top:4, right:6, width:7, height:7, borderRadius:'50%', background:'#DC2626' }}/>}
                </button>
                {/* Hover submenu — only when panel is collapsed */}
                {isHovered && collapsed && (
                  <div style={{ position:'absolute', left:62, top:0, background:'#252321', border:'1px solid #2E2C29', borderRadius:8, padding:'6px 0', minWidth:180, boxShadow:'0 8px 24px rgba(0,0,0,0.4)', zIndex:400 }}>
                    <p style={{ fontSize:9, fontWeight:700, color:'#4A4845', textTransform:'uppercase', letterSpacing:'.1em', padding:'2px 12px 6px' }}>{group}</p>
                    {items.map(item => (
                      <button key={item.key} onClick={() => { setPage(item.key); setPanelGroup(group); setHoveredGroup(null) }}
                        style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'6px 12px', background: page===item.key ? '#2E2C29' : 'transparent', border:'none', cursor:'pointer', textAlign:'left', transition:'background 0.1s' }}
                        onMouseEnter={e=>e.currentTarget.style.background='#2E2C29'}
                        onMouseLeave={e=>e.currentTarget.style.background=page===item.key?'#2E2C29':'transparent'}>
                        <Icon name={item.key} size={12}/>
                        <span style={{ fontSize:12, color: page===item.key ? '#1A7A4A' : '#D0CCC6', fontWeight: page===item.key ? 600 : 400 }}>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
        <button onClick={() => setCollapsed(c => !c)} title={collapsed?'Expand':'Collapse'}
          style={{ width:'100%', padding:'8px 0', display:'flex', alignItems:'center', justifyContent:'center', background:'transparent', border:'none', borderTop:'1px solid #2E2C29', cursor:'pointer', color:'#4A4845', transition:'color 0.12s' }}
          onMouseEnter={e=>e.currentTarget.style.color='#9A948C'}
          onMouseLeave={e=>e.currentTarget.style.color='#4A4845'}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            {collapsed ? <><polyline points="4,3 8,7 4,11"/><polyline points="8,3 12,7 8,11"/></> : <><polyline points="10,3 6,7 10,11"/><polyline points="6,3 2,7 6,11"/></>}
          </svg>
        </button>
      </div>

      {/* ── RIGHT PANEL (200px) ── */}
      <div style={{ width: collapsed?0:200, background:'#1C1A18', borderRight: collapsed?'none':'1px solid #2E2C29', display:'flex', flexDirection:'column', overflow:'hidden', transition:'width 0.2s ease', flexShrink:0 }}>
        <div style={{ height:52, padding:'0 14px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #2E2C29', flexShrink:0 }}>
          <div>
            <p style={{ fontSize:13, fontWeight:700, color:'#F5F1EA', lineHeight:1 }}>{panelGroup}</p>
            <p style={{ fontSize:9, color:'#4A4845', marginTop:2 }}>{panelItems.length} modules</p>
          </div>
        </div>
        <nav style={{ flex:1, overflowY:'auto', padding:'5px 0' }}>
          {panelItems.map(item => (
            <NavItem key={item.key} item={item} active={page===item.key} pendingCount={pendingCount} onClick={() => setPage(item.key)}/>
          ))}
        </nav>
        <div style={{ padding:'7px 10px 10px', borderTop:'1px solid #2E2C29', flexShrink:0 }}>
          <div onClick={onProjectClick}
            style={{ display:'flex', alignItems:'center', gap:8, background:'#252321', border:'1px solid #2E2C29', borderRadius:7, padding:'7px 10px', cursor:'pointer', transition:'border-color 0.15s' }}
            onMouseEnter={e=>e.currentTarget.style.borderColor='#1A7A4A'}
            onMouseLeave={e=>e.currentTarget.style.borderColor='#2E2C29'}>
            <div style={{ width:22, height:22, borderRadius:5, background:'#1A7A4A', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#fff', flexShrink:0 }}>
              {selectedProject ? selectedProject.name.charAt(0).toUpperCase() : '—'}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ fontSize:11, fontWeight:600, color:'#F5F1EA', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1.2 }}>
                {selectedProject ? selectedProject.name : '— No project —'}
              </p>
              <p style={{ fontSize:8.5, color:'#4A4845', lineHeight:1 }}>{selectedProject ? 'Active project' : 'Select a project'}</p>
            </div>
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#4A4845" strokeWidth="1.5" strokeLinecap="round">
              <polyline points="2,4 6,8 10,4"/>
            </svg>
          </div>
        </div>
      </div>
    </aside>
  )
}

// ══════════════════════════════════════════════════════════
// PROJECT DROPDOWN
// ══════════════════════════════════════════════════════════
function ProjectDropdown({ projects, selectedProject, onSelect }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const PROJECT_COLORS = ['#1A7A4A','#7C3AED','#0369A1','#B45309','#0F766E','#DC2626','#D97706']

  return (
    <div ref={ref} style={{ position:'relative', flexShrink:0 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 8px', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.07)', borderRadius:6, color:'#D1D5DB', fontSize:11.5, cursor:'pointer', fontFamily:'inherit', transition:'all .12s' }}
        onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,.1)'}
        onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,.06)'}>
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 4h3l1.5-2h6L14 4v9a1 1 0 01-1 1H3a1 1 0 01-1-1V4z"/></svg>
        <span style={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {selectedProject ? selectedProject.name : 'Select Project'}
        </span>
        <span style={{ color:'rgba(255,255,255,.3)', fontSize:9 }}>▾</span>
      </button>

      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 8px)', left:0, zIndex:400, background:'#1C1A18', border:'1px solid rgba(255,255,255,.07)', borderRadius:10, width:272, boxShadow:'0 8px 28px rgba(0,0,0,.35)', padding:'4px 0', overflow:'hidden' }}>
          <div style={{ padding:'8px 14px 5px', fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.12em', color:'rgba(255,255,255,.28)' }}>Projects</div>
          <div onClick={() => { onSelect(null); setOpen(false) }}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', cursor:'pointer', borderLeft:`2px solid ${!selectedProject ? '#1A7A4A' : 'transparent'}`, background: !selectedProject ? 'rgba(26,122,74,.14)' : 'transparent', transition:'background .1s' }}
            onMouseEnter={e=>{ if(selectedProject) e.currentTarget.style.background='rgba(255,255,255,.06)' }}
            onMouseLeave={e=>{ if(selectedProject) e.currentTarget.style.background='transparent' }}>
            <div style={{ width:22, height:22, borderRadius:3, background:'#374151', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#fff', flexShrink:0 }}>A</div>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'#fff' }}>All Projects</div>
              <div style={{ fontSize:10, color:'rgba(255,255,255,.35)' }}>Portfolio view</div>
            </div>
          </div>
          <div style={{ height:1, background:'rgba(255,255,255,.06)', margin:'4px 0' }}/>
          {projects.map((p, i) => {
            const isOn = selectedProject?.id === p.id
            const color = PROJECT_COLORS[i % PROJECT_COLORS.length]
            return (
              <div key={p.id} onClick={() => { onSelect(p); setOpen(false) }}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', cursor:'pointer', borderLeft:`2px solid ${isOn ? '#1A7A4A' : 'transparent'}`, background: isOn ? 'rgba(26,122,74,.14)' : 'transparent', transition:'background .1s' }}
                onMouseEnter={e=>{ if(!isOn) e.currentTarget.style.background='rgba(255,255,255,.06)' }}
                onMouseLeave={e=>{ if(!isOn) e.currentTarget.style.background='transparent' }}>
                <div style={{ width:22, height:22, borderRadius:3, background:color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#fff', flexShrink:0 }}>
                  {p.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize:10, color:'rgba(255,255,255,.35)' }}>{p.organizations?.name || ''}</div>
                </div>
                {isOn && <span style={{ color:'#1A7A4A', fontSize:11 }}>✓</span>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════
// APP SHELL
// ══════════════════════════════════════════════════════════
function Denied() {
  return <div style={{padding:60,textAlign:'center',color:'#9A948C',fontSize:13}}>Access denied — you don't have permission to view this page.</div>
}

function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  // `page` is initialized (once, via the lazy useState initializer -- runs
  // during first render, before paint, so there is no launchpad flash) from
  // the current URL so a hard refresh or direct link into
  // /estimation/... lands correctly. This is the ONLY place page's source
  // of truth changes; every existing case in the switch below, and all 49
  // existing pages, are completely untouched.
  const [page, setPageState] = useState(() =>
    location.pathname.startsWith('/estimation') ? 'estimation' : 'launchpad'
  )
  // Keeps `page` in sync with the URL after the initial render too --
  // the lazy initializer above only runs once. Without this, a
  // navigate('/estimation') call updates the URL but `page` (still
  // 'launchpad' from first render) doesn't follow until some other
  // state change forces a re-render, which is exactly the race that
  // caused "No routes matched location /" on the very first click.
  // Does not touch any of the other 49 cases -- setPage() below is
  // still what drives navigation for state-only pages; this effect
  // only reconciles the one boundary (estimation <-> everything else)
  // that now has a real URL behind it.
  useEffect(() => {
    const isEstimation = location.pathname.startsWith('/estimation')
    setPageState((prev) => {
      if (isEstimation) return prev === 'estimation' ? prev : 'estimation'
      return prev === 'estimation' ? 'launchpad' : prev
    })
  }, [location.pathname])
  // Single navigation entry point for the sites that can ever target
  // 'estimation' (NavItem, the expanded panel-group button, and the Command
  // Palette). All other existing setPage() call sites are untouched --
  // their targets (launchpad, costcontrol, sibling tabs, notifications) can
  // never be 'estimation', so routing them through here would be a no-op
  // change with added risk for zero benefit.
  function setPage(key) {
    if (key === 'estimation') {
      navigate('/estimation')
    } else {
      if (page === 'estimation') navigate('/')
      setPageState(key)
    }
  }
  // Set by ReleaseManagement's "Open in Cost Control" action — read once by
  // CostControl.jsx on mount to pre-select that Release, then cleared, so
  // navigating to Cost Control normally afterward doesn't keep re-applying
  // a stale selection. This is a plain prop-passing mechanism (this app has
  // no URL router), not a query parameter.
  const [pendingReleaseId, setPendingReleaseId] = useState(null)
  const [pendingPKContext, setPendingPKContext] = useState(null)
  const [selectedProject, setSelectedProject] = useState(null)
  // IPC page-access guard now uses the same project-permission model as
  // IPC.jsx's own button-level checks, instead of the legacy
  // can.approve_ipc/can.add_ipc role arrays (which never included
  // commercial_engineer/director/cost_control's new names -- confirmed
  // by inspection to be the exact cause of "Access denied" for all
  // three real IPC roles). Hook called once here at AppShell level
  // (Hooks cannot be called inside the plain renderPage() function
  // below), re-evaluated automatically whenever selectedProject changes.
  const { can: ipcCan } = useProjectAuth(selectedProject?.id)
  // Purchase Orders page-access guard, same pattern as ipcCan above --
  // replaces the legacy can.approve_po/procRoles gate (role names like
  // director_operations/director_commercial never matched the new
  // project_members role names procurement/cost_control/director).
  const { can: poCan } = useProjectAuth(selectedProject?.id)
  // Purchase Requisitions page-access guard, same pattern as poCan
  // above -- replaces the legacy procRoles (can.add_po) gate, which
  // has no relationship to PR's own roles (site_engineer/cost_control/
  // procurement).
  const { can: prCan } = useProjectAuth(selectedProject?.id)
  // Analytics Studio page-access guard, same pattern as ipcCan/poCan/prCan
  // above -- this route previously had zero access protection at all.
  const { can: analyticsCan } = useProjectAuth(selectedProject?.id)
  // Subcontracts page-access guard, same pattern as analyticsCan above
  // -- replaces the legacy can.approve_subcontract gate (Legacy role
  // names like director_operations never matched the new
  // project_members role contract_manager).
  const { can: subcontractCan } = useProjectAuth(selectedProject?.id)
  // Inventory Management page-access guard, same pattern as the above --
  // replaces the legacy procRoles/can.edit_budget gate.
  const { can: inventoryCan } = useProjectAuth(selectedProject?.id)
  // RFQ Quotations page-access guard, same pattern as the above.
  const { can: rfqCan } = useProjectAuth(selectedProject?.id)
  // Store + Material Master page-access guards, same pattern as the
  // above. VendorMaster uses no useProjectAuth guard here (vendors is
  // organization-wide, no project_id -- the page itself checks
  // has_global_permission('Vendor.View') via RPC internally).
  const { can: storeCan } = useProjectAuth(selectedProject?.id)
  const { can: materialCan } = useProjectAuth(selectedProject?.id)
  // Batch 3A read-only pages guard -- single useProjectAuth instance
  // shared across all five new View-only permissions (Budget.View,
  // PO.View, Reports.View, Documents.Govern, ExecutiveReport.View),
  // per the approved simplification (avoids five separate hook calls
  // for permissions that never need independent loading states).
  const { can: viewCan } = useProjectAuth(selectedProject?.id)
  // Batch 3B guard -- 9 modules (RiskRegister, EOT, TechnicalChange,
  // EngineeringChange/RisksChanges, FinalAccount, DocumentCenter, BOQ,
  // Accrual, Retention).
  const { can: b3bCan } = useProjectAuth(selectedProject?.id)
  // Batch 3C-2 guard -- Forecast (Direct/Indirect) + Release Management.
  const { can: forecastCan } = useProjectAuth(selectedProject?.id)
  // Operational-page gates (Cost Codes, Budget, Indirect Budget, Cost
  // Control, QS Tracking, Payroll, Petty Cash, Finance, Scheduling,
  // Productivity, Site Allocation): project membership -> project role ->
  // granular role_permissions, same model as every batch above.
  // user_profiles.role (the legacy can.* helpers) is NOT consulted for
  // ordinary users. Platform admins keep access even when no project is
  // selected (useProjectAuth returns an empty set without a project) via
  // the platform-level is_platform_admin() -- the same authority used by
  // the admin-only pages and every database policy. Server-side
  // enforcement (RLS/triggers via has_permission()) remains authoritative;
  // this gate is UX only.
  const { can: opsCan } = useProjectAuth(selectedProject?.id)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  useEffect(() => {
    let cancelled = false
    supabase.rpc('is_platform_admin').then(({ data }) => { if (!cancelled) setIsPlatformAdmin(!!data) })
    return () => { cancelled = true }
  }, [])
  const opsGate = (permission) => isPlatformAdmin || opsCan(permission)
  const [showExecReport,  setShowExecReport]  = useState(false)
  const [showExecReportConfig, setShowExecReportConfig] = useState(false)
  const [projects, setProjects] = useState([])
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [notifCount, setNotifCount] = useState(0)
  const { can } = useAuth()
  // Bugfix: usePendingCount() was removed — it queried approval_actions,
  // the confirmed-dead approval system (0 rows, no live writer, per the
  // approved PendingApprovalsPage.jsx cleanup). pendingCount is kept as a
  // static 0 rather than removing it from every downstream consumer
  // (NavItem, TwoLevelSidebar's Workflow group badge), since those
  // components' badge-display logic is generic and may be reused by a
  // future, real notification source — only the dead data source itself
  // was removed, not the display capability.
  const pendingCount = 0

  // Load notification count on project change
  useEffect(() => {
    if (!selectedProject?.id) { setNotifCount(0); return }
    Promise.all([
      supabase.from('purchase_orders').select('id',{count:'exact',head:true}).eq('project_id',selectedProject.id).eq('status','draft'),
      supabase.from('ipc_certificates').select('id',{count:'exact',head:true}).eq('project_id',selectedProject.id).in('status',['submitted','under_review']),
    ]).then(([pos, ipcs]) => {
      setNotifCount((pos.count||0) + (ipcs.count||0))
    })
  }, [selectedProject])

  useEffect(() => {
    supabase.from('projects').select('id,name,code,contract_value,currency,status,organizations(name)').order('name')
      .then(({ data }) => setProjects(data || []))
  }, [])

  // ⌘K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setPaletteOpen(p => !p) }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  function handleProjectSelect(project) { setSelectedProject(project); setPage('dashboard') }

  const allRoles = true // viewer+

  function renderPage() {
    switch (page) {
      case 'launchpad':             return <HomeLaunchpad onNavigate={setPage} selectedProject={selectedProject} projects={projects}/>
      case 'workspace':             return <HomeWorkspace onNavigate={setPage} selectedProject={selectedProject} onSelectProject={handleProjectSelect}/>
      case 'dashboard':             return allRoles ? <Dashboard initProject={selectedProject}/> : <Denied/>
      case 'organizations':         return can.is_admin ? <Organizations/> : <Denied/>
      case 'projects':              return can.is_admin ? <Projects onSelectProject={handleProjectSelect} selectedProject={selectedProject}/> : <Denied/>
      case 'cost-codes':            return opsGate('CostCodes.View') ? <CostCodes initProject={selectedProject}/> : <Denied/>
      case 'boq':                   return b3bCan('BOQ.View') ? <BOQ initProject={selectedProject}/> : <Denied/>
      case 'budget':                return opsGate('Budget.View') ? <Budget initProject={selectedProject}/> : <Denied/>
      case 'indirect':              return opsGate('Budget.View') ? <IndirectBudget initProject={selectedProject}/> : <Denied/>
      case 'budget-summary':        return viewCan('Budget.View') ? <BudgetSummary projectId={selectedProject?.id}/> : <Denied/>
      case 'vendor-master':         return <VendorMaster/>
      case 'material-master':       return (materialCan('Material.Create') || materialCan('Material.Edit') || materialCan('Material.Delete')) ? <MaterialMaster initProject={selectedProject}/> : <Denied/>
      case 'purchase-requisitions': return (prCan('PR.Create') || prCan('PR.Submit') || prCan('PR.ApproveCostControl') || prCan('PR.ApproveProcurement') || prCan('PR.Delete') || prCan('PR.Edit') || prCan('EquipmentPR.Create') || prCan('EquipmentPR.Submit') || prCan('EquipmentPR.ApproveCostControl') || prCan('EquipmentPR.ApproveProcurement') || prCan('EquipmentPR.Delete') || prCan('EquipmentPR.Edit')) ? <PurchaseRequisitions initProject={selectedProject}/> : <Denied/>
      case 'rfq':                   return (rfqCan('RFQ.Create') || rfqCan('RFQ.Edit') || rfqCan('RFQ.Delete') || rfqCan('RFQ.Award')) ? <RFQQuotations initProject={selectedProject}/> : <Denied/>
      case 'purchase-orders':       return (poCan('PO.Create') || poCan('PO.Submit') || poCan('PO.ApproveCostControl') || poCan('PO.ApproveDirector') || poCan('PO.Delete') || poCan('PO.Edit')) ? <PurchaseOrders initProject={selectedProject}/> : <Denied/>
      case 'store':                 return storeCan('Store.View') ? <Store initProject={selectedProject}/> : <Denied/>
      case 'inventory-management':  return (inventoryCan('Inventory.Create') || inventoryCan('Inventory.Submit') || inventoryCan('Inventory.ApproveCostControl') || inventoryCan('Inventory.Receive')) ? <InventoryManagement initProject={selectedProject}/> : <Denied/>
      case 'subcontracts':          return (subcontractCan('Subcontract.Create') || subcontractCan('Subcontract.Edit') || subcontractCan('Subcontract.Delete') || subcontractCan('Subcontract.Submit') || subcontractCan('Subcontract.ApproveCostControl') || subcontractCan('Subcontract.ApproveDirector')) ? <Subcontracts projectId={selectedProject?.id} onNavigate={setPage}/> : <Denied/>
      case 'ipc':                   return (ipcCan('IPC.Create') || ipcCan('IPC.Submit') || ipcCan('IPC.ApproveCostControl') || ipcCan('IPC.ApproveDirector') || ipcCan('IPC.Delete')) ? <IPC projectId={selectedProject?.id} onNavigate={setPage}/> : <Denied/>
      case 'retention':             return b3bCan('Retention.View') ? <RetentionManagement projectId={selectedProject?.id}/> : <Denied/>
      case 'payroll':               return opsGate('Payroll.View') ? <Payroll projectId={selectedProject?.id}/> : <Denied/>
      case 'finance':               return opsGate('Finance.View') ? <FinanceLandingPage onNavigate={setPage}/> : <Denied/>
      case 'finance-detail':        return opsGate('Finance.View') ? <Finance projectId={selectedProject?.id}/> : <Denied/>
      case 'scheduling':            return opsGate('Scheduling.View') ? <Scheduling projectId={selectedProject?.id}/> : <Denied/>
      case 'accrual':               return b3bCan('Accrual.View') ? <Accrual projectId={selectedProject?.id}/> : <Denied/>
      case 'direct-forecast':       return forecastCan('Forecast.View') ? <DirectForecast projectId={selectedProject?.id}/> : <Denied/>
      case 'indirect-forecast':     return forecastCan('Forecast.View') ? <IndirectForecast projectId={selectedProject?.id}/> : <Denied/>
      case 'release-management':    return forecastCan('Release.View') ? <ReleaseManagement projectId={selectedProject?.id}
                                       onOpenInCostControl={(releaseId) => { setPendingReleaseId(releaseId); setPage('costcontrol') }}/> : <Denied/>
      case 'petty-cash':            return opsGate('PettyCash.View') ? <PettyCash projectId={selectedProject?.id}/> : <Denied/>
      case 'costcontrol':           return opsGate('CostControl.View') ? <CostControl projectId={selectedProject?.id}
                                       initialReleaseId={pendingReleaseId} onInitialReleaseConsumed={() => setPendingReleaseId(null)}/> : <Denied/>
      case 'po-reconciliation':     return viewCan('PO.View') ? <POReconciliation initProject={selectedProject}/> : <Denied/>
      case 'cost-assessment':       return viewCan('Budget.View') ? <CostAssessment projectId={selectedProject?.id}/> : <Denied/>
      case 'rate-analysis':         return viewCan('Budget.View') ? <RateAnalysis initProject={selectedProject}/> : <Denied/>
      case 'productivity':          return opsGate('Productivity.View') ? <LabourProductivity initProject={selectedProject}/> : <Denied/>
      case 'equipment-productivity': return opsGate('Productivity.View') ? <EquipmentProductivity initProject={selectedProject}/> : <Denied/>
      case 'site-allocation':        return opsGate('SiteAllocation.View') ? <SiteAllocationDraft initProject={selectedProject}/> : <Denied/>
      case 'qs-tracking':            return opsGate('QSTracking.View') ? <QSQuantityTracking initProject={selectedProject}/> : <Denied/>
      case 'risks-changes':          return b3bCan('EngineeringChange.View') ? <RisksChanges initProject={selectedProject}/> : <Denied/>
      case 'users':                 return can.manage_users ? <UsersPage/> : <Denied/>
      // Global page (not project-scoped): authorization is checked
      // inside CurrencyExchangeRatesPage itself via is_platform_admin()
      // OR is_company_operator(), since neither `can` (legacy
      // user_profiles.role) nor useProjectAuth() (requires a
      // selectedProject) can express company_operator access correctly.
      case 'currency-fx':           return <CurrencyExchangeRatesPage/>
      // Global page, same pattern as currency-fx above: authorization
      // checked inside SubscriptionManagementPage itself via
      // is_platform_admin().
      case 'subscription-management': return <SubscriptionManagementPage/>
      case 'product-knowledge':     return allRoles ? <ProductKnowledge initialContext={pendingPKContext} onInitialContextConsumed={() => setPendingPKContext(null)}/> : <Denied/>
      case 'permissions':            return can.is_admin ? <PermissionsPage/> : <Denied/>
      case 'monthly-cost':           return viewCan('Reports.View') ? <MonthlyActualCost initProject={selectedProject}/> : <Denied/>
      case 'analytics-studio':       return analyticsCan('Analytics.View') ? <AnalyticsStudio projectId={selectedProject?.id}/> : <Denied/>
      case 'alerts':                return viewCan('Reports.View') ? <AlertsVariances initProject={selectedProject} onNavigate={setPage}/> : <Denied/>
      case 'documents':             return allRoles ? <DocumentCenter initProject={selectedProject}/> : <Denied/>
      case 'document-governance':   return viewCan('Documents.Govern') ? <DocumentGovernance initProject={selectedProject}/> : <Denied/>
      case 'final-account':         return b3bCan('FinalAccount.View') ? <FinalAccount projectId={selectedProject?.id}/> : <Denied/>
      case 'risk-register':         return b3bCan('RiskRegister.View') ? <RiskRegister projectId={selectedProject?.id}/> : <Denied/>
      case 'eot-register':          return b3bCan('EOT.View') ? <EOTRegister projectId={selectedProject?.id}/> : <Denied/>
      case 'change-register':       return b3bCan('TechnicalChange.View') ? <TechnicalChangeRegister projectId={selectedProject?.id}/> : <Denied/>
      // New 50th case. Estimation owns everything under /estimation/* via
      // its own <Routes> tree (BrowserRouter removed from Estimation's own
      // App.jsx per the approved slice); this is the sole mount point.
      // Estimation's own AuthProvider/ProtectedRoute/AppShell are untouched
      // and still fully control access to its pages -- CostSpace does not
      // merge auth here.
      case 'estimation':            return (
                                       <Routes>
                                         <Route path="estimation/*" element={<EstimationApp selectedProject={selectedProject}/>}/>
                                       </Routes>
                                     )
      default:                      return <HomeLaunchpad onNavigate={setPage} selectedProject={selectedProject} projects={projects}/>
    }
  }

  const isHome = page === 'launchpad'

  return (
    <NavigationContextIdentityProvider navKey={page} route={location.pathname}>
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', background:'#F3F4F6' }}>

      {/* ── Global Header ── */}
      <div style={{ height:48, background:'#1C1A18', borderBottom:'1px solid rgba(255,255,255,.07)', display:'flex', alignItems:'center', gap:10, padding:'0 14px', flexShrink:0, zIndex:200 }}>
        {/* Logo */}
        <div style={{ display:'flex', alignItems:'center', gap:8, paddingRight:14, borderRight:'1px solid rgba(255,255,255,.07)', flexShrink:0 }}>
          <QubexMark size={22}/>
          <span style={{ fontSize:13, fontWeight:700, color:'#fff', letterSpacing:'-.01em', fontFamily:'Space Grotesk,Inter,sans-serif' }}>Cost<span style={{color:'#5DADE2'}}>Space</span></span>
        </div>

        {/* Project Dropdown */}
        <ProjectDropdown
          projects={projects}
          selectedProject={selectedProject}
          onSelect={(p) => setSelectedProject(p)}
        />

        {/* Search */}
        <div style={{ flex:1, maxWidth:340, margin:'0 12px' }}>
          <button onClick={() => setPaletteOpen(true)}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'5px 12px', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.07)', borderRadius:6, color:'#9CA3AF', fontSize:12, cursor:'pointer', fontFamily:'inherit', transition:'all .12s' }}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,.09)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,.06)'}>
            <Icon name="search" size={13}/>
            Search pages & modules…
            <span style={{ marginLeft:'auto', display:'flex', gap:2 }}>
              <span style={{ padding:'1px 5px', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', borderRadius:3, fontSize:9, color:'rgba(255,255,255,.35)' }}>⌘</span>
              <span style={{ padding:'1px 5px', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', borderRadius:3, fontSize:9, color:'rgba(255,255,255,.35)' }}>K</span>
            </span>
          </button>
        </div>

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          {/* Executive Report button -- was previously shown with zero
              permission check (a real access gap), now gated on
              ExecutiveReport.View. */}
          {selectedProject && viewCan('ExecutiveReport.View') && (
            <button onClick={() => setShowExecReport(true)}
              style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', background:'rgba(26,122,74,.15)', border:'1px solid rgba(26,122,74,.3)', borderRadius:6, color:'#6EE7B7', cursor:'pointer', fontSize:11, fontWeight:700, fontFamily:'inherit', transition:'all .12s' }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(26,122,74,.25)'; e.currentTarget.style.color='#fff' }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(26,122,74,.15)'; e.currentTarget.style.color='#6EE7B7' }}
              title="Executive Summary Report">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:12,height:12}}>
                <rect x="2" y="2" width="12" height="12" rx="1.5"/><line x1="5" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="9" y2="9"/>
              </svg>
              Exec Report
            </button>
          )}
          {/* Contextual Product Knowledge doorway */}
          <ProductKnowledgeContextLink onOpenProductKnowledge={(target) => { setPendingPKContext(target); setPage('product-knowledge') }}/>
          {/* Product Knowledge -- standalone reader */}
          <button onClick={() => setPage('product-knowledge')}
            title="Product Knowledge"
            style={{ height:32, padding:'0 10px', display:'flex', alignItems:'center', gap:6, background: page==='product-knowledge' ? 'rgba(255,255,255,.12)' : 'transparent', border:'1px solid transparent', borderRadius:6, color: page==='product-knowledge' ? '#fff' : '#9CA3AF', cursor:'pointer', fontSize:12, fontWeight:600, transition:'all .12s' }}
            onMouseEnter={e=>{ if (page!=='product-knowledge') { e.currentTarget.style.background='rgba(255,255,255,.07)'; e.currentTarget.style.color='#fff' } }}
            onMouseLeave={e=>{ if (page!=='product-knowledge') { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#9CA3AF' } }}>
            📘 Product Knowledge
          </button>
          {/* Help button */}
          <button onClick={() => setHelpOpen(o => !o)}
            style={{ width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', background:'transparent', border:'1px solid transparent', borderRadius:6, color:'#9CA3AF', cursor:'pointer', fontSize:14, fontWeight:700, transition:'all .12s' }}
            onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,.07)'; e.currentTarget.style.color='#fff' }}
            onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#9CA3AF' }}>
            ?
          </button>
          {/* Notification bell */}
          <button onClick={() => setNotifOpen(o => !o)}
            style={{ position:'relative', width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', background:'transparent', border:'1px solid transparent', borderRadius:6, color:'#9CA3AF', cursor:'pointer', transition:'all .12s' }}
            onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,.07)'; e.currentTarget.style.color='#fff' }}
            onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#9CA3AF' }}>
            <Icon name="bell" size={15}/>
            {notifCount > 0 && (
              <span style={{ position:'absolute', top:3, right:3, minWidth:16, height:16, borderRadius:8, background:'#DC2626', color:'#fff', fontSize:9, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>
                {notifCount > 9 ? '9+' : notifCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>
        <main style={{ flex:1, overflowY:'auto', background: isHome ? '#fff' : '#F3F4F6', display:'flex', flexDirection:'column' }}>
          {/* Breadcrumb — shown on all pages except Home */}
          {!isHome && (
            <div style={{ height:34, display:'flex', alignItems:'center', gap:6, padding:'0 24px', borderBottom:'1px solid #E5E7EB', background:'#fff', flexShrink:0, fontSize:11.5 }}>
              <button onClick={() => setPage('launchpad')}
                style={{ display:'flex', alignItems:'center', gap:5, color:'#6B7280', background:'none', border:'none', cursor:'pointer', fontSize:11.5, fontFamily:'inherit', padding:0 }}
                onMouseEnter={e=>e.currentTarget.style.color='#0F1017'}
                onMouseLeave={e=>e.currentTarget.style.color='#6B7280'}>
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="10,3 6,8 10,13"/></svg>
                Home
              </button>
              <span style={{ color:'#D1D5DB' }}>›</span>
              {/* Group label */}
              <span style={{ color:'#9CA3AF' }}>{NAV.find(n => n.key === page)?.group}</span>
              <span style={{ color:'#D1D5DB' }}>›</span>
              <span style={{ color:'#0F1017', fontWeight:500 }}>
                {page === 'estimation'
                  ? (location.pathname.includes('/revision/') ? 'Revision'
                     : location.pathname.includes('/tender/') ? 'Tender Workspace'
                     : 'Estimation')
                  : (NAV.find(n => n.key === page)?.label || page)}
              </span>
              {/* Sibling pages in same group */}
              {(() => {
                const currentGroup = NAV.find(n => n.key === page)?.group
                const siblings = NAV.filter(n => n.group === currentGroup)
                if (siblings.length <= 1) return null
                return (
                  <>
                    <div style={{ width:1, height:14, background:'#E5E7EB', margin:'0 6px' }}/>
                    <div style={{ display:'flex', alignItems:'center', gap:2 }}>
                      {siblings.map(s => (
                        <button key={s.key} onClick={() => setPage(s.key)}
                          style={{ padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight: s.key===page ? 600 : 400,
                            background: s.key===page ? '#1A7A4A' : 'transparent',
                            color: s.key===page ? '#fff' : '#6B7280',
                            border: s.key===page ? 'none' : '1px solid transparent',
                            cursor:'pointer', whiteSpace:'nowrap' }}
                          onMouseEnter={e=>{ if(s.key!==page) e.currentTarget.style.background='#F3F4F6' }}
                          onMouseLeave={e=>{ if(s.key!==page) e.currentTarget.style.background='transparent' }}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </>
                )
              })()}
            </div>
          )}
          <div style={{ flex:1, overflowY:'auto' }}>
            {renderPage()}
          </div>
        </main>
      </div>

      {/* ── Overlays ── */}
      <ContextualHelp open={helpOpen} onClose={() => setHelpOpen(false)}/>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={(key) => { setPage(key); setPaletteOpen(false) }}
        selectedProject={selectedProject}
      />
      <NotificationTray
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        onNavigate={(key) => { setPage(key); setNotifOpen(false) }}
        projectId={selectedProject?.id}
      />

      {/* ── Reports ── */}
      {showExecReport && (
        <ExecutiveReport
          projectId={selectedProject?.id}
          onClose={() => setShowExecReport(false)}
          onOpenConfig={() => setShowExecReportConfig(true)}
        />
      )}
      {showExecReportConfig && (
        <div style={{position:'fixed',inset:0,background:'#F3F4F6',overflowY:'auto',zIndex:10000}}>
          <ExecutiveReportConfig
            projectId={selectedProject?.id}
            projectName={selectedProject?.name}
            onClose={() => setShowExecReportConfig(false)}
          />
        </div>
      )}
    </div>
    </NavigationContextIdentityProvider>
  )
}

function AppInner() {
  const { user, profile, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // Local to the signed-out Landing Page route only -- CostSpace remains
  // Invite-Only (AuthContext.jsx's signUp() is untouched); this state
  // just tracks whether the Trial Request modal is open.
  const [trialModalOpen, setTrialModalOpen] = useState(false)
  if (loading) return <div style={{ minHeight:'100vh', background:'#1C1A18', display:'flex', alignItems:'center', justifyContent:'center', color:'#9A948C', fontSize:13 }}>Loading…</div>
  // Public marketing route: only the root path, and only when signed
  // out. An unauthenticated visit to any OTHER path (including /login,
  // and every existing in-app path) falls through to the exact
  // pre-existing behavior below (LoginPage) -- this branch and the
  // Trial Request modal below it are the only additions to this
  // function; nothing else in it changed.
  if (!user && location.pathname === '/') return (
    <>
      <LandingPage onStartTrial={() => setTrialModalOpen(true)} onLogin={() => navigate('/login')} />
      <TrialRequestModal open={trialModalOpen} onClose={() => setTrialModalOpen(false)} />
    </>
  )
  if (!user) return <LoginPage/>
  // Phase 2: closes the critical gap where an authenticated-but-profile-less
  // user (uninvited signup that slipped through, or a deactivated account
  // signed out mid-loadProfile) fell through to the app shell with an
  // implicit 'viewer' role instead of being denied entirely.
  if (!profile) return <LoginPage/>
  // The actual access-enforcement point (previously has_costspace_access()
  // existed but was never called from anywhere in the frontend -- this
  // is the fix). Platform admins bypass inside SubscriptionGate itself;
  // everyone else's organization must have valid entitlement (trial,
  // active, or past_due) or they see the blocked screen instead of
  // AppShell. No subscription logic lives here -- the gate defers
  // entirely to the database functions.
  return (
    <SubscriptionGate organizationId={profile.organization_id}>
      <AppShell/>
    </SubscriptionGate>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppInner/>
      </BrowserRouter>
    </AuthProvider>
  )
}
